// ── Lead Capture Service ──────────────────────────────────────────────────────
// Handles: Airtable record creation + Resend email delivery
//
// Required env vars (Netlify dashboard → Site configuration → Environment variables):
//   REACT_APP_AIRTABLE_API_KEY   — Airtable personal access token
//   REACT_APP_AIRTABLE_BASE_ID   — e.g. appXXXXXXXXXXXXXX
//   REACT_APP_AIRTABLE_TABLE     — table name (default: "Leads")
//   RESEND_API_KEY               — Resend API key (server-side only, never sent to browser)
//   FROM_EMAIL                   — verified sender (default: reports@aorinsurance.com)
//
// Emails are sent via the Netlify serverless function at /.netlify/functions/send-email
// to avoid CORS restrictions that block direct Resend API calls from the browser.

const fmt = n =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const INDUSTRY_LABELS = {
  gc:'General Contractor', roofing:'Roofing', painting:'Painting & Coatings',
  electrical:'Electrical', plumbing:'Plumbing', hvac:'HVAC / Mechanical',
  framing:'Framing / Carpentry', concrete:'Concrete & Masonry', demolition:'Demolition',
  landscaping:'Landscaping / Hardscape', fabrication:'Custom Fabrication',
  restoration:'Fire / Water Restoration', solar:'Solar Installation', paving:'Paving / Asphalt',
  re_investor:'Real Estate Investor', re_commercial:'Commercial Property Owner',
  re_flipper:'Fix & Flip Investor', re_rental:'Rental Portfolio Owner',
  re_developer:'Real Estate Developer', re_hoa:'HOA / Condo Association',
  re_broker:'Real Estate Brokerage', re_pm:'Property Management',
  other:'Other / Specialty',
};

// ── Airtable ──────────────────────────────────────────────────────────────────
export async function saveToAirtable(data, savings) {
  const key   = process.env.REACT_APP_AIRTABLE_API_KEY;
  const base  = process.env.REACT_APP_AIRTABLE_BASE_ID;
  const table = process.env.REACT_APP_AIRTABLE_TABLE || 'Leads';
  if (!key || !base) return;

  return fetch(`https://api.airtable.com/v0/${base}/${encodeURIComponent(table)}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${key}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fields: {
        'Company Name':      data.companyName || '',
        'Contact Name':      data.contactName || '',
        'Email':             data.email || '',
        'Phone':             data.phone || '',
        'Industry':          INDUSTRY_LABELS[data.industry] || data.industry || '',
        'State':             data.primaryState || '',
        'Annual Revenue':    parseInt(data.revenue) || 0,
        'Employees':         parseInt(data.employees) || 0,
        'Fleet Size':        parseInt(data.fleetSize) || 0,
        'Subcontractors':    parseInt(data.subcontractors) || 0,
        'Claims (3yr)':      parseInt(data.claims) || 0,
        'Current Carrier':   data.carrier || '',
        'Estimated Savings': savings,
        'Source':            'Risk Analyzer Tool',
        'Submitted At':      new Date().toISOString(),
      },
    }),
  });
}

// ── Resend helper — routed through Netlify serverless function ─────────────────
// Direct browser → Resend calls are blocked by CORS. The serverless function
// runs in Node.js and holds the API key server-side so it's never in the bundle.
async function sendEmail(payload) {
  return fetch('/.netlify/functions/send-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// ── Prospect email ────────────────────────────────────────────────────────────
export async function sendProspectEmail(data, savings, results) {
  if (!data.email) return;

  const firstName = (data.contactName || '').split(' ')[0] || 'there';
  const stateName = results.stateInfo?.name || data.primaryState || '';
  const indLabel  = INDUSTRY_LABELS[data.industry] || 'your business';
  const totalCur  = Object.values(results.cur).reduce((a, b) => a + b, 0);
  const savPct    = totalCur ? Math.round((savings / totalCur) * 100) : 0;
  const riskColor = results.riskScore >= 60 ? '#DC2626' : results.riskScore >= 35 ? '#D97706' : '#16A34A';
  const riskLabel = results.riskScore >= 60 ? 'High' : results.riskScore >= 35 ? 'Moderate' : 'Low';

  const gapRows = (results.gaps || []).slice(0, 3).map(g => `
    <tr>
      <td style="padding:10px 14px;border-bottom:1px solid #F1F5F9;font-size:13px;">
        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${g.level === 'red' ? '#DC2626' : '#D97706'};margin-right:8px;vertical-align:middle;"></span>
        <strong style="color:#0F172A;">${g.title}</strong>
        <div style="font-size:12px;color:#64748B;margin-top:3px;padding-left:16px;">${g.desc}</div>
      </td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width"/></head>
<body style="margin:0;padding:0;background:#F8FAFC;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
<div style="max-width:600px;margin:0 auto;padding:20px 16px;">

  <!-- Header -->
  <div style="background:#0F172A;border-radius:14px 14px 0 0;padding:22px 28px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;">
    <div>
      <div style="color:#fff;font-size:21px;font-weight:900;letter-spacing:-0.02em;">AOR Insurance</div>
      <div style="color:rgba(255,255,255,0.4);font-size:11px;letter-spacing:0.1em;text-transform:uppercase;margin-top:3px;">Commercial Risk Report</div>
    </div>
    <div style="font-size:11px;color:rgba(255,255,255,0.35);">${new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
  </div>

  <!-- Savings hero -->
  <div style="background:linear-gradient(135deg,#0A1628 0%,#132D55 100%);padding:36px 28px;text-align:center;">
    <div style="color:rgba(255,255,255,0.45);font-size:10px;text-transform:uppercase;letter-spacing:0.14em;margin-bottom:10px;">Estimated Annual Premium Savings</div>
    <div style="color:#fff;font-size:54px;font-weight:900;letter-spacing:-0.04em;line-height:1;margin-bottom:8px;">${fmt(savings)}</div>
    <div style="color:rgba(255,255,255,0.4);font-size:12px;">${indLabel}${stateName ? ' · ' + stateName : ''}</div>
    <div style="display:inline-block;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.15);border-radius:99px;padding:7px 18px;margin-top:14px;">
      <span style="color:rgba(255,255,255,0.5);font-size:12px;">Risk Score: </span>
      <span style="color:${riskColor};font-size:13px;font-weight:800;">${Math.round(results.riskScore)}/100 (${riskLabel})</span>
    </div>
  </div>

  <!-- Body -->
  <div style="background:#fff;padding:28px;border:1px solid #E2E8F0;border-top:none;">
    <p style="margin:0 0 18px;font-size:15px;color:#0F172A;line-height:1.6;">Hi <strong>${firstName}</strong>,</p>
    <p style="margin:0 0 24px;font-size:14px;color:#475569;line-height:1.75;">
      Thank you for completing your risk analysis with AOR Insurance. Based on your profile for
      <strong style="color:#0F172A;">${data.companyName}</strong>, we identified an estimated
      <strong style="color:#2563EB;">${fmt(savings)}/year</strong> in available savings —
      approximately <strong>${savPct}%</strong> of your current estimated premiums.
    </p>

    <!-- Premium comparison -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px;">
      <tr>
        <td style="padding:4px;">
          <div style="background:#FEF2F2;border-radius:10px;padding:16px 14px;text-align:center;">
            <div style="font-size:10px;color:#94A3B8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:5px;">Current Est.</div>
            <div style="font-size:22px;font-weight:800;color:#DC2626;">${fmt(totalCur)}</div>
          </div>
        </td>
        <td style="padding:4px;">
          <div style="background:#F0FDF4;border-radius:10px;padding:16px 14px;text-align:center;">
            <div style="font-size:10px;color:#94A3B8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:5px;">AOR Optimized</div>
            <div style="font-size:22px;font-weight:800;color:#16A34A;">${fmt(totalCur - savings)}</div>
          </div>
        </td>
        <td style="padding:4px;">
          <div style="background:#EFF6FF;border-radius:10px;padding:16px 14px;text-align:center;">
            <div style="font-size:10px;color:#94A3B8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:5px;">Reduction</div>
            <div style="font-size:22px;font-weight:800;color:#2563EB;">${savPct}%</div>
          </div>
        </td>
      </tr>
    </table>

    ${gapRows ? `
    <!-- Coverage gaps -->
    <div style="margin-bottom:24px;">
      <div style="font-size:13px;font-weight:700;color:#92400E;background:#FEF3C7;border-radius:8px;padding:10px 14px;margin-bottom:2px;">
        ⚠️ Coverage Gaps Identified
      </div>
      <table width="100%" cellpadding="0" cellspacing="0">${gapRows}</table>
    </div>
    ` : ''}

    <!-- CTA -->
    <div style="background:#F8FAFF;border-radius:12px;padding:22px 24px;border:1px solid #BFDBFE;text-align:center;margin-bottom:24px;">
      <div style="font-size:15px;font-weight:700;color:#0F172A;margin-bottom:8px;">Ready to capture these savings?</div>
      <div style="font-size:13px;color:#475569;margin-bottom:18px;line-height:1.65;">
        Schedule a free 15-minute review with an AOR specialist. No obligation — just clarity on what your coverage should cost.
      </div>
      <a href="https://aorinsurance.com/book"
        style="display:inline-block;background:#B8963E;color:#fff;text-decoration:none;padding:13px 30px;border-radius:9px;font-weight:700;font-size:14px;letter-spacing:0.02em;">
        📅 Book a Free Meeting →
      </a>
    </div>

    <!-- Footer -->
    <div style="font-size:11px;color:#94A3B8;text-align:center;line-height:1.8;padding-top:16px;border-top:1px solid #F1F5F9;">
      <strong style="color:#475569;">AOR Insurance</strong> — Top 100 Independent Agency<br/>
      <a href="https://aorinsurance.com" style="color:#2563EB;text-decoration:none;">aorinsurance.com</a>
      &nbsp;·&nbsp; branden@aorinsurance.com &nbsp;·&nbsp; (352) 901-1853<br/>
      Licensed in All 50 States
    </div>
  </div>

</div>
</body></html>`;

  return sendEmail({
    to: [data.email],
    subject: `Your AOR Insurance Risk Report — ${fmt(savings)} in Estimated Annual Savings`,
    html,
  });
}

// ── Internal notification ─────────────────────────────────────────────────────
export async function sendInternalNotification(data, savings) {

  const rows = [
    ['👤 Contact Name',   data.contactName || '—'],
    ['🏢 Company',         data.companyName || '—'],
    ['📧 Email',           data.email || '—'],
    ['📞 Phone',           data.phone || '—'],
    ['🏭 Industry',        INDUSTRY_LABELS[data.industry] || data.industry || '—'],
    ['📍 State',           data.primaryState || '—'],
    ['💰 Annual Revenue',  data.revenue ? fmt(parseInt(data.revenue)) : '—'],
    ['👷 Employees',       data.employees || '—'],
    ['🚗 Fleet Size',      data.fleetSize || '0'],
    ['📋 Subcontractors',  data.subcontractors || '0'],
    ['⚠️ Claims (3yr)',    data.claims || '0'],
    ['🏦 Current Carrier', data.carrier || '—'],
  ].map(([label, value], i) => `
    <tr style="background:${i % 2 === 0 ? '#F8FAFC' : '#fff'};">
      <td style="padding:10px 14px;font-size:12px;color:#64748B;font-weight:600;width:42%;border-bottom:1px solid #F1F5F9;">${label}</td>
      <td style="padding:10px 14px;font-size:13px;color:#0F172A;font-weight:700;border-bottom:1px solid #F1F5F9;">${value}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/></head>
<body style="margin:0;padding:0;background:#F8FAFC;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
<div style="max-width:540px;margin:0 auto;padding:20px 16px;">

  <div style="background:#0F172A;border-radius:12px 12px 0 0;padding:18px 22px;">
    <div style="color:#fff;font-size:17px;font-weight:800;">🔔 New Lead — AOR Risk Analyzer</div>
    <div style="color:rgba(255,255,255,0.4);font-size:11px;margin-top:4px;">
      ${new Date().toLocaleString('en-US',{month:'long',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'})}
    </div>
  </div>

  <div style="background:#fff;padding:22px;border:1px solid #E2E8F0;border-top:none;border-radius:0 0 12px 12px;">

    <!-- Savings callout -->
    <div style="background:linear-gradient(135deg,#EFF6FF,#F0FDF4);border-radius:10px;padding:18px;text-align:center;margin-bottom:20px;border:1px solid #BFDBFE;">
      <div style="font-size:10px;color:#64748B;text-transform:uppercase;letter-spacing:0.12em;margin-bottom:5px;">Savings Opportunity</div>
      <div style="font-size:38px;font-weight:900;color:#2563EB;letter-spacing:-0.03em;line-height:1;">
        ${fmt(savings)}<span style="font-size:14px;color:#94A3B8;font-weight:400;">/yr</span>
      </div>
    </div>

    <!-- Lead data table -->
    <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-bottom:20px;">
      ${rows}
    </table>

    <!-- Action buttons -->
    <div style="display:flex;gap:8px;flex-wrap:wrap;">
      <a href="mailto:${data.email}"
        style="display:inline-block;background:#2563EB;color:#fff;text-decoration:none;padding:11px 20px;border-radius:8px;font-weight:700;font-size:13px;">
        ✉️ Reply to Lead
      </a>
      ${data.phone ? `
      <a href="tel:${data.phone.replace(/\D/g,'')}"
        style="display:inline-block;background:#16A34A;color:#fff;text-decoration:none;padding:11px 20px;border-radius:8px;font-weight:700;font-size:13px;">
        📞 Call Now
      </a>` : ''}
    </div>

  </div>
</div>
</body></html>`;

  return sendEmail({
    to: ['branden@aorinsurance.com'],
    subject: `🔔 New Lead: ${data.companyName} — ${fmt(savings)} | ${data.primaryState || 'N/A'}`,
    html,
  });
}

// ── Main entry point ──────────────────────────────────────────────────────────
export async function submitLead(data, savings, results) {
  try {
    await Promise.allSettled([
      saveToAirtable(data, savings),
      sendProspectEmail(data, savings, results),
      sendInternalNotification(data, savings),
    ]);
  } catch (e) {
    // Fire-and-forget — never surface lead capture errors to the user
    console.error('[AOR] Lead submission error:', e);
  }
}
